import classNames from 'classnames';
import React from 'react';
import styles from './ButtonGroup.module.scss';
import Tooltip from './Tooltip';

export interface ButtonGroupOption<TValue> {
  value: TValue;
  label: string | React.ReactElement;
  tooltip?: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  'data-testid'?: string;
}

export interface ButtonGroupProps<TValue>
  extends Omit<React.HTMLProps<HTMLDivElement>, 'size' | 'onChange'> {
  size?: 'xsmall' | 'small' | 'medium' | 'large';
  options: ReadonlyArray<ButtonGroupOption<TValue>>;
  stretch?: boolean;
  activeValue: TValue;
  onChange: (value: TValue, event: React.SyntheticEvent) => void;
}

const ButtonGroup = <TValue extends string | number | boolean | null>(
  props: ButtonGroupProps<TValue>
) => {
  const { size = 'small', options, activeValue, stretch, onChange, ...rest } = props;
  const groupClassName = classNames(
    styles.buttonGroup,
    styles[`size-${size}`],
    {
      [styles.stretch]: stretch,
    },
    props.className
  );
  return (
    <div {...rest} className={groupClassName}>
      {options.map(({ value, label, tooltip, icon, disabled, ...rest }) =>
        maybeWrapTooltip(
          tooltip,
          value,
          <button
            type="button"
            className={classNames(styles.button, {
              [styles.active]: value === activeValue,
              [styles.disabled]: disabled,
            })}
            key={String(value)}
            onClick={e => !disabled && onChange(value, e)}
            data-testid={rest['data-testid'] || `button-group-${value}`}
          >
            {icon}
            <span>{label}</span>
          </button>
        )
      )}
    </div>
  );
};
ButtonGroup.displayName = 'ButtonGroup';

function maybeWrapTooltip(
  tooltip: React.ReactNode | string | undefined,
  key: string | number | boolean | null,
  element: React.ReactElement
) {
  if (tooltip) {
    return (
      <Tooltip key={String(key)} tooltip={tooltip}>
        {element}
      </Tooltip>
    );
  }
  return element;
}

export default ButtonGroup;
